import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Check, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { api } from "../../lib/api";
import { buildProfileContext } from "../../lib/profile";
import { useApp } from "../../context/AppContext";
import { useS, ACT_FR } from "../../lib/simple";

const clean = (t) => (t || "").replace(/\\([#*_])/g, "$1").replace(/\*\*(.*?)\*\*/g, "$1").replace(/^#+\s*/gm, "").trim();

export function StarterCreate({ action, answers, draftId, onDraft, onPublished }) {
  const { s } = useS();
  const { data, user, demo } = useApp();
  const isAccount = user && !demo;
  const [topic, setTopic] = useState("");
  const [withOffer, setWithOffer] = useState(false);
  const [offer, setOffer] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(!!(draftId && isAccount));
  const [dirty, setDirty] = useState(false);
  const timer = useRef();
  const idRef = useRef(draftId);
  const textRef = useRef("");
  const alive = useRef(true);

  useEffect(() => {
    if (draftId && isAccount) {
      api.get(`/deliverables/${draftId}`).then(({ data: d }) => { if (alive.current) setText(d.text || ""); })
        .catch(() => {}).finally(() => alive.current && setLoadingDraft(false));
    }
    return () => {
      alive.current = false;
      if (timer.current) { clearTimeout(timer.current); persist(textRef.current); }
    };
  }, []); // eslint-disable-line

  const persist = async (value) => {
    if (!isAccount || !value) return;
    timer.current = null;
    if (alive.current) setSaving(true);
    const payload = { type: action.content_type, title: action.title, text: value };
    try {
      if (idRef.current) await api.put(`/deliverables/${idRef.current}`, payload);
      else { const { data: d } = await api.post("/deliverables", payload); idRef.current = d.id; onDraft(d.id); }
    } catch (e) { console.error("Draft save failed:", e); }
    finally { if (alive.current) setSaving(false); }
  };

  const generate = async () => {
    if (dirty && !window.confirm(s("regen_confirm"))) return;
    clearTimeout(timer.current); timer.current = null;
    setLoading(true);
    const brief = [action.title + ".", action.brief, `Activité : ${ACT_FR[answers.activity] || "commerce local"}.`,
      topic && `À mettre en avant : ${topic}.`,
      withOffer && offer ? `Inclure cette offre : ${offer}.` : "Ne propose aucune réduction ni promotion.",
      "Texte court, simple et chaleureux, prêt à copier, sans titre ni commentaire autour."].filter(Boolean).join(" ");
    try {
      const { data: r } = await api.post("/ai/content", {
        content_type: action.content_type, tone: "chaleureux", topic: brief, action: "generate",
        business_name: data?.onboarding?.business_name || "mon commerce", profile_context: buildProfileContext(data),
      });
      const v = clean(r.content);
      setText(v); textRef.current = v; setDirty(false);
      await persist(v);
    } catch { toast.error(s("gen_err")); }
    finally { setLoading(false); }
  };

  const onEdit = (v) => {
    setText(v); textRef.current = v; setDirty(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(v), 800);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success(action.copied); }
    catch { toast.error(s("copy_err")); }
  };

  return (
    <div data-testid="starter-create">
      <h1 className="font-display font-black text-xl sm:text-2xl leading-tight">{action.title}</h1>
      {loadingDraft && <div className="mt-6 space-y-2" data-testid="starter-draft-loading"><div className="skeleton h-4 w-3/4" /><div className="skeleton h-4 w-full" /><div className="skeleton h-40 w-full rounded-xl" /></div>}
      {!text && !loadingDraft && (
        <div className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-medium" htmlFor="starter-topic">{s("topic_label")}</label>
            <Input id="starter-topic" data-testid="starter-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={s("topic_ph")} className="mt-2 h-12 bg-white/5 border-white/10 text-base" />
          </div>
          <label className="flex items-center gap-3 min-h-[44px] cursor-pointer text-sm" data-testid="starter-offer-toggle">
            <Checkbox checked={withOffer} onCheckedChange={(v) => setWithOffer(!!v)} /> {s("offer_toggle")}
          </label>
          {withOffer && <Input data-testid="starter-offer" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder={s("offer_ph")} className="h-12 bg-white/5 border-white/10 text-base" />}
          <Button onClick={generate} disabled={loading} data-testid="starter-generate" className="w-full rounded-full h-14 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-base">
            {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> {s("generating")}</> : <><Sparkles size={18} className="mr-1.5" /> {s("generate")}</>}
          </Button>
        </div>
      )}
      {text && (
        <div className="mt-5">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-sm font-medium">{s("your_text")}</p>
            <span className="text-[11px] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1" data-testid="starter-draft-status">
              {saving ? <><Loader2 size={11} className="animate-spin" /> {s("saving")}</> : isAccount && <><Check size={11} className="text-emerald-400" /> {s("draft_saved")}</>}
            </span>
          </div>
          <Textarea data-testid="starter-text" value={text} onChange={(e) => onEdit(e.target.value)} rows={9} className="bg-white/[0.04] border-white/10 text-[15px] leading-relaxed" />
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">{s("review_hint")}</p>
          <Button onClick={copy} data-testid="starter-copy" className="w-full mt-5 rounded-full h-14 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-base">
            <Copy size={18} className="mr-1.5" /> {s("copy")}
          </Button>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button onClick={generate} disabled={loading} data-testid="starter-regenerate" variant="outline" className="rounded-full h-12 border-white/10 bg-white/5 text-sm">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <><RefreshCw size={15} className="mr-1" /> {s("regenerate")}</>}
            </Button>
            <Button onClick={onPublished} data-testid="starter-published" variant="outline" className="rounded-full h-12 border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm">
              <Check size={16} className="mr-1" /> {s("published")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
